import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { AxiosError } from "axios";
import {
  insuranceProvidersService,
  CreateInsuranceProviderInput,
  UpdateInsuranceProviderInput,
} from "../../services/admin/insuranceProviders.service";

/**
 * Extract a user-friendly error message from API errors.
 * Specifically handles duplicate key constraint violations.
 */
function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (error instanceof AxiosError) {
    const responseData = error.response?.data;

    // Check for duplicate key error from backend
    if (responseData?.error === "DUPLICATE_NAME") {
      return responseData.message || "A provider with this name already exists";
    }

    // Check for PostgreSQL unique constraint violation (code 23505)
    // This handles cases where backend doesn't catch the error
    if (
      error.response?.status === 500 &&
      (responseData?.message?.includes("duplicate key") ||
        responseData?.message?.includes("unique constraint") ||
        responseData?.error?.includes("duplicate key") ||
        responseData?.error?.includes("unique constraint") ||
        error.message?.includes("duplicate key"))
    ) {
      return "A provider with this name already exists. Please use a different name.";
    }

    // Return backend-provided message if available
    if (responseData?.message) {
      return responseData.message;
    }
  }

  // Fallback to error message or default
  if (error instanceof Error) {
    return error.message || defaultMessage;
  }

  return defaultMessage;
}

export function useInsuranceProvidersAdmin() {
  return useQuery({
    queryKey: ["admin", "insurance_providers"],
    queryFn: insuranceProvidersService.getAll,
  });
}

export function useInsuranceProvider(id?: string) {
  return useQuery({
    queryKey: ["admin", "insurance_providers", id],
    queryFn: () => insuranceProvidersService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateInsuranceProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateInsuranceProviderInput) =>
      insuranceProvidersService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "insurance_providers"],
      });
      queryClient.invalidateQueries({ queryKey: ["insurance_providers"] });
      queryClient.invalidateQueries({ queryKey: ["insurance-providers"] });
      queryClient.invalidateQueries({
        queryKey: ["insurance-plan-types-by-provider"],
      });
      notifications.show({
        title: "Success",
        message: "Insurance provider created successfully",
        color: "green",
      });
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "Error",
        message: getErrorMessage(error, "Failed to create insurance provider"),
        color: "red",
      });
    },
  });
}

export function useUpdateInsuranceProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateInsuranceProviderInput) =>
      insuranceProvidersService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "insurance_providers"],
      });
      queryClient.invalidateQueries({ queryKey: ["insurance_providers"] });
      notifications.show({
        title: "Success",
        message: "Insurance provider updated successfully",
        color: "green",
      });
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "Error",
        message: getErrorMessage(error, "Failed to update insurance provider"),
        color: "red",
      });
    },
  });
}

export function useToggleInsuranceProviderActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      insuranceProvidersService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "insurance_providers"],
      });
      queryClient.invalidateQueries({ queryKey: ["insurance_providers"] });
      notifications.show({
        title: "Success",
        message: "Insurance provider status updated successfully",
        color: "green",
      });
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "Error",
        message: getErrorMessage(
          error,
          "Failed to update insurance provider status"
        ),
        color: "red",
      });
    },
  });
}

export function useDeleteInsuranceProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => insuranceProvidersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "insurance_providers"],
      });
      queryClient.invalidateQueries({ queryKey: ["insurance_providers"] });
      notifications.show({
        title: "Success",
        message: "Insurance provider deleted successfully",
        color: "green",
      });
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "Error",
        message: getErrorMessage(error, "Failed to delete insurance provider"),
        color: "red",
      });
    },
  });
}
